import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import CustomSelect from '../../ui/CustomSelect';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { DocumentType } from './DocumentTypeTableRow';
import { ArchiveCategory } from '../../../api/archiveCategoryApi';

interface DocumentTypeModalProps {
  open: boolean;
  data: DocumentType | null;
  categories: ArchiveCategory[];
  saving: boolean;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  onDataChange: (data: DocumentType) => void;
}

export default function DocumentTypeModal({
  open,
  data,
  categories,
  saving,
  onClose,
  onSave,
  onDataChange,
}: DocumentTypeModalProps) {
  const [errors, setErrors] = useState<{
    document_code?: string;
    document_name?: string;
    category_id?: string;
  }>({});

  useEffect(() => {
    // Reset errors when modal opens/closes
    if (open) {
      setErrors({});
    }
  }, [open]);

  if (!data) return null;

  const handleFieldChange = (field: keyof DocumentType, value: any) => {
    onDataChange({ ...data, [field]: value });
    // Clear error when user types/selects
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    const newErrors: typeof errors = {};
    let hasError = false;

    // Validate required fields
    if (!data.document_code || data.document_code.trim() === '') {
      newErrors.document_code = 'Kode dokumen wajib diisi';
      hasError = true;
    }

    if (!data.document_name || data.document_name.trim() === '') {
      newErrors.document_name = 'Nama dokumen wajib diisi';
      hasError = true;
    }

    if (!data.category_id || data.category_id === 0 || String(data.category_id).trim() === '') {
      newErrors.category_id = 'Kategori wajib dipilih';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    onSave(e);
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{data.id ? 'Edit' : 'Tambah'} Jenis Dokumen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Kode Dokumen <span className="text-red-500">*</span>
              </Label>
              <Input
                value={data.document_code || ''}
                onChange={e => handleFieldChange('document_code', e.target.value)}
                className={errors.document_code ? 'border-red-500' : ''}
              />
              {errors.document_code && (
                <p className="text-sm text-red-500">{errors.document_code}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                Nama Dokumen <span className="text-red-500">*</span>
              </Label>
              <Input
                value={data.document_name || ''}
                onChange={e => handleFieldChange('document_name', e.target.value)}
                className={errors.document_name ? 'border-red-500' : ''}
              />
              {errors.document_name && (
                <p className="text-sm text-red-500">{errors.document_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                Kategori <span className="text-red-500">*</span>
              </Label>
              <CustomSelect
                value={String(data.category_id || '')}
                onChange={v => handleFieldChange('category_id', v ? parseInt(v) : '')}
                options={categories.map(cat => ({
                  value: String(cat.id),
                  label: cat.category_name,
                }))}
                placeholder="Pilih kategori"
                width="w-full"
                color="blue"
                error={!!errors.category_id}
              />
              {errors.category_id && <p className="text-sm text-red-500">{errors.category_id}</p>}
            </div>
            <div className="space-y-2">
              <Label>Max File Size (MB)</Label>
              <Input
                type="number"
                value={data.max_file_size_mb || 5}
                onChange={e => handleFieldChange('max_file_size_mb', parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Mandatory</Label>
              <Switch
                checked={data.is_mandatory || false}
                onCheckedChange={v => handleFieldChange('is_mandatory', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Perlu Validasi</Label>
              <Switch
                checked={data.requires_validation || false}
                onCheckedChange={v => handleFieldChange('requires_validation', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Perlu Nomor Dokumen</Label>
              <Switch
                checked={data.requires_number || false}
                onCheckedChange={v => handleFieldChange('requires_number', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Perlu Tanggal Kadaluarsa</Label>
              <Switch
                checked={data.requires_expiry || false}
                onCheckedChange={v => handleFieldChange('requires_expiry', v)}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <Label>Status Aktif</Label>
              <Switch
                checked={data.is_active || false}
                onCheckedChange={v => handleFieldChange('is_active', v)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Batal
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
